import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { useUpdateStakingPlanMutation } from "../../redux/stakingPlanApi";
import { useTranslation } from "react-i18next";

function EditStakingPlan({ show, onHide, cryptocurrencies, plan }) {
    const { t } = useTranslation();
    const [updateStakingPlan, { isLoading }] = useUpdateStakingPlanMutation();

    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (plan) {
            setFormData({
                id: plan.id,
                cryptocurrency_id: plan.cryptocurrency_id || "",
                daily_reward_percent: plan.daily_reward_percent || "",
                staking_type: plan.staking_type || "",
                locking_days: plan.locking_days || "",
                daily_reward_cryptocurrency_id: plan.daily_reward_cryptocurrency_id || "",
                minimum_daily_reward_withdraw_limit: plan.minimum_daily_reward_withdraw_limit || "",
                referral_purchase_bonus_percent: plan.referral_purchase_bonus_percent || "",
                referral_matching_bonus_percent: plan.referral_matching_bonus_percent || "",
                stake_fee_cryptocurrency_id: plan.stake_fee_cryptocurrency_id || null,
                stake_fee_amount: plan.stake_fee_amount || null,
                stake_fee_type: plan.stake_fee_type || null,
                stake_fee_status: plan.stake_fee_status || "OFF",
                total_staking_supply: plan.total_staking_supply || "",
                staking_receive_wallet_address: plan.staking_receive_wallet_address || "",
                status: plan.status || "active"
            });
        }
    }, [plan]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            const result = await updateStakingPlan(formData).unwrap();
            if (result.status) {
                toast.success(result.message || t('updated_successfully'));
                onHide();
            } else {
                toast.error(result.message || t('failed_to_update'));
            }
        } catch (error) {
            toast.error(error?.data?.message || t('error_occurred'));
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('edit_staking_plan')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('cryptocurrency')}</Form.Label>
                                <Form.Select name="cryptocurrency_id" value={formData.cryptocurrency_id} onChange={handleChange}>
                                    <option value="">{t('select')}</option>
                                    {cryptocurrencies.map(c => <option key={c.id} value={c.id}>{c.coin_name} ({c.coin_symbol})</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('daily_reward_percent')}</Form.Label>
                                <Form.Control type="number" name="daily_reward_percent" value={formData.daily_reward_percent} onChange={handleChange} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('staking_type')}</Form.Label>
                                <Form.Select name="staking_type" value={formData.staking_type} onChange={handleChange}>
                                    <option value="">{t('select')}</option>
                                    <option value="swap_staking">{t('swap_staking')}</option>
                                    <option value="general_staking">{t('general_staking')}</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('locking_days')}</Form.Label>
                                <Form.Control type="number" name="locking_days" value={formData.locking_days} onChange={handleChange} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('total_staking_supply')}</Form.Label>
                                <Form.Control type="number" name="total_staking_supply" value={formData.total_staking_supply} onChange={handleChange} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('daily_reward_cryptocurrency')}</Form.Label>
                                <Form.Select name="daily_reward_cryptocurrency_id" value={formData.daily_reward_cryptocurrency_id} onChange={handleChange}>
                                    <option value="">{t('select')}</option>
                                    {cryptocurrencies.map(c => <option key={c.id} value={c.id}>{c.coin_name} ({c.coin_symbol})</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('min_daily_reward_withdraw_limit')}</Form.Label>
                                <Form.Control type="number" name="minimum_daily_reward_withdraw_limit" value={formData.minimum_daily_reward_withdraw_limit} onChange={handleChange} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('staking_receive_wallet_address')}</Form.Label>
                                <Form.Control type="text" name="staking_receive_wallet_address" value={formData.staking_receive_wallet_address} onChange={handleChange} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('referral_purchase_bonus_percent')}</Form.Label>
                                <Form.Control type="number" name="referral_purchase_bonus_percent" value={formData.referral_purchase_bonus_percent} onChange={handleChange} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('referral_matching_bonus_percent')}</Form.Label>
                                <Form.Control type="number" name="referral_matching_bonus_percent" value={formData.referral_matching_bonus_percent} onChange={handleChange} />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('stake_fee_status')}</Form.Label>
                                <Form.Select name="stake_fee_status" value={formData.stake_fee_status} onChange={handleChange}>
                                    <option value="ON">ON</option>
                                    <option value="OFF">OFF</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        {formData.stake_fee_status === "ON" && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('stake_fee_cryptocurrency')}</Form.Label>
                                    <Form.Select name="stake_fee_cryptocurrency_id" value={formData.stake_fee_cryptocurrency_id} onChange={handleChange}>
                                        <option value="">{t('select')}</option>
                                        {cryptocurrencies.map(c => <option key={c.id} value={c.id}>{c.coin_name} ({c.coin_symbol})</option>)}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        )}
                    </Row>
                    {formData.stake_fee_status === "ON" && (
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('stake_fee_type')}</Form.Label>
                                    <Form.Select name="stake_fee_type" value={formData.stake_fee_type} onChange={handleChange}>
                                        <option value="">{t('select')}</option>
                                        <option value="fixed">{t('fixed')}</option>
                                        <option value="percentage">{t('percentage')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('stake_fee_amount')}</Form.Label>
                                    <Form.Control type="number" name="stake_fee_amount" value={formData.stake_fee_amount} onChange={handleChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('status')}</Form.Label>
                                <Form.Select name="status" value={formData.status} onChange={handleChange}>
                                    <option value="active">{t('active')}</option>
                                    <option value="inactive">{t('inactive')}</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>{t('close')}</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>{t('update')}</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default EditStakingPlan;
